import { useEffect, useRef, useState, useCallback, type PointerEvent as ReactPointerEvent } from 'react';
import { Volume2 } from 'lucide-react';
import { hapticFeedback } from '@telegram-apps/sdk-react';
import type { ModuleMeta } from '../../harness/ModuleRegistry';
import { AtmosphericLayer } from '../_shared/AtmosphericLayer';
import { Sigil } from '../_nav/Sigil';

// 04 Penalty Kicker — Wave 3 visual rework (see WAVE_3_BRIEF.md §4.4 A-F).
// PixiJS continues to draw the field + ball + keeper + net. Type-heavy chrome
// (scoreboard digits, hint copy) moves to an HTML overlay so Lora glyph quality
// stays font-fidelity. Hint-arc lives in a sibling SVG overlay so the gradient
// stroke and dasharray render without polluting the Pixi scene graph.
//
// r2 (Igor 2026-06-11): swipe-to-kick is now LIVE. Previously the pointer
// handlers only drove the hint-arc preview and `pointerup` just cleared `aim`
// — there was no impulse, flight, or scoring (the module shipped as the GDD
// step-1 "scene scaffold"). We now: flick the ball along the swipe vector with
// a synthetic Z-arc + shadow (Pixi ticker, no matter-js needed for a single
// straight-line penalty), run a lightweight keeper AI, detect goal vs save,
// and reset for the NEXT shot. Attempts are INFINITE — no per-game cap, no
// lock-out; the scoreboard keeps a running goals tally instead.

// Defensive Telegram-SDK wrapper — browser preview falls through silently per
// DESIGN.md §1.4 (matches 04b/04c/07 trySync convention).
function trySync<T>(fn: () => T): T | undefined {
  try {
    return fn();
  } catch {
    return undefined;
  }
}

export const meta: ModuleMeta = {
  id: '04-game-kicker',
  title: 'Penalty Kicker · swipe-to-kick',
  status: 'approved',
  viewport: '390x844',
};

const FIELD_W = 390;
const FIELD_H = 580;
const BALL_X = FIELD_W / 2;
// Ball moved up from 460 → 320 per Igor — clears the bottom area for the hint
// and keeps the ball closer to the goalkeeper visually.
const BALL_Y = 320;
const GUARDIAN_HOME_X = FIELD_W / 2;
const GUARDIAN_Y = 85;

// ─── Kick tuning (GDD §5 "swipe-to-kick", §6 physics) ───
// Swipe must travel ≥ MIN_SWIPE px to count (GDD §5: 40px → fail-feedback,
// no shot). Power is the swipe length clamped to MAX_SWIPE. We DON'T pull in
// matter-js here: a penalty is a single straight flight ball→goal, so a
// ticker-driven linear interpolation with a synthetic sine Z-arc is lighter,
// deterministic, and FPS-cheap (perf budget §14) — no physics world to step.
const MIN_SWIPE = 40;
const MAX_SWIPE = 220;
// Goal-line geometry shared with the Pixi goal drawing below (goalTop/posts).
// The ball "scores" if its flight terminus lands inside the mouth in X and is
// near the goal line in Y, AND the keeper didn't cover that third.
const GOAL_MOUTH_LEFT = 103; // just inside the left post (post at x=100, 3px)
const GOAL_MOUTH_RIGHT = 287; // just inside the right post (post at x=290)
const GOAL_LINE_Y = 110; // matches GOAL_LINE_Y used in the field draw
// Flight duration scales with power: harder = faster (GDD §9 in-flight 0.35→0.18s).
const FLIGHT_MS_MIN = 360;
const FLIGHT_MS_MAX = 620;
const RESET_DELAY_MS = 900; // GDD §9 reset beat (shortened — infinite play).

type ShotResult = 'goal' | 'save' | 'miss';

const COLORS = {
  surfaceMain: 0x1f1f1f,
  textPrimary: 0xf5f5f5,
  borderHairline: 0x5f5f5f,
  actionPrimary: 0x0071e4,
  accentCyan: 0x0dd5fd,
} as const;

// Split-flap digit — wrapper holds the cyan halo, inner span rotates on each
// numeric change. Key on `value` ensures React re-mounts the inner span and
// re-runs the flip keyframe even when the same value is set twice in a row.
function ScoreDigit({ value, side }: { value: number; side: 'left' | 'right' }) {
  const label = side === 'left' ? 'ТЫ' : 'ВРАТАРЬ';
  const [flipKey, setFlipKey] = useState(0);
  const prev = useRef(value);

  useEffect(() => {
    if (prev.current !== value) {
      prev.current = value;
      setFlipKey((k) => k + 1);
    }
  }, [value]);

  return (
    <div className="module-04-scoreboard__side">
      <span className="module-04-scoreboard__label">{label}</span>
      <span className="module-04-scoreboard__digit" aria-label={`${label} ${value}`}>
        <span
          key={flipKey}
          className="module-04-scoreboard__digit-inner module-04-scoreboard__digit-inner--flip"
        >
          {value}
        </span>
      </span>
    </div>
  );
}

export default function KickerModule() {
  const canvasHostRef = useRef<HTMLDivElement>(null);
  const keeperShadowRef = useRef<HTMLDivElement>(null);
  // Running tally — INFINITE play (Igor 2026-06-11: "убрать попытки, сделать их
  // бесконечными"). `you` counts goals scored, `keeper` counts saves/misses
  // stopped. Never resets, never locks the input. The kick effect bumps these
  // via setScore, the split-flap digit animates on change.
  const [score, setScore] = useState({ you: 0, keeper: 0 });

  // ─── Hint-arc preview state ───
  // We render an SVG parabola from BALL to the active pointer position.
  // Stored as canvas-relative coords so the overlay maps 1:1 with the pixi stage.
  const [aim, setAim] = useState<{ x: number; y: number } | null>(null);
  // Mirror of `aim` in a ref. The pointer-up handler reads THIS (not the `aim`
  // state) to fire the shot — reading state in a `useCallback([])` would be a
  // stale closure (it'd see the initial null). The ref always holds the latest
  // release point, so the kick direction is correct regardless of render timing.
  const aimRef = useRef<{ x: number; y: number } | null>(null);
  const setAimBoth = useCallback((v: { x: number; y: number } | null) => {
    aimRef.current = v;
    setAim(v);
  }, []);

  // Bridge from React pointer events → the Pixi scene's kick routine. The
  // effect below populates `.current` once the stage is built; the pointer-up
  // handler calls it with the resolved swipe target (canvas coords). Kept in a
  // ref (not state) so firing a shot never re-renders the component tree.
  const kickRef = useRef<((targetX: number, targetY: number) => void) | null>(null);
  // True while a shot is in flight / resetting — new pointers are ignored so a
  // touch mid-flight can't queue a second kick (GDD §12 "касание во время
  // полёта — игнорируется").
  const busyRef = useRef(false);

  const toCanvas = (e: ReactPointerEvent<HTMLDivElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const sx = FIELD_W / rect.width;
    const sy = FIELD_H / rect.height;
    return { x: (e.clientX - rect.left) * sx, y: (e.clientY - rect.top) * sy };
  };

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (busyRef.current) return;
      setAimBoth(toCanvas(e));
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [setAimBoth],
  );

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (aimRef.current === null) return;
      setAimBoth(toCanvas(e));
    },
    [setAimBoth],
  );

  const onPointerUp = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      // Fire the shot from the LAST pointer position relative to the ball. The
      // hint-arc the user just saw was drawn BALL→pointer, so the shot direction
      // matches it exactly. Read the latest release point from aimRef (state is
      // async). Swipe-length gate lives inside the kick routine.
      const target = aimRef.current;
      setAimBoth(null);
      if (target && !busyRef.current) {
        kickRef.current?.(target.x, target.y);
      }
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* pointer may already have been released */
      }
    },
    [setAimBoth],
  );

  useEffect(() => {
    let appRef: unknown = null;
    let removeTicker: (() => void) | null = null;
    let cancelled = false;

    void (async () => {
      const PIXI = await import('pixi.js');
      if (cancelled) return;

      const app = new PIXI.Application();
      await app.init({
        width: FIELD_W,
        height: FIELD_H,
        backgroundAlpha: 0, // let the atmosphere/page bg show through
        resolution: window.devicePixelRatio || 1,
        autoDensity: true,
        antialias: true,
      });
      if (cancelled) {
        app.destroy(true);
        return;
      }
      appRef = app;

      // ─── FIELD LINEWORK ───
      // Wave 3: thicker outer pen-area outline (3px), slim hairline inner guide
      // following pen-arc curve, goal line bumped to 2px, cyan dot at penalty
      // spot (replaces neutral white) for §9.8 corner-glint family accent.
      // GOAL_LINE_Y is the module-level constant (110) — shared with goal
      // detection so the linework and scoring agree on the goal line.
      const PEN_AREA_DEPTH = 170;
      const SIX_YARD_DEPTH = 50;
      const outerStyle = { color: COLORS.borderHairline, width: 3, alpha: 0.85 };
      const guideStyle = { color: COLORS.borderHairline, width: 1, alpha: 0.45 };
      const innerStyle = { color: COLORS.borderHairline, width: 1.5, alpha: 0.7 };

      const penArea = new PIXI.Graphics();
      penArea.rect(60, GOAL_LINE_Y, 270, PEN_AREA_DEPTH).stroke(outerStyle);
      app.stage.addChild(penArea);

      // Inner 1px hairline guide — inset 6px from the pen area for the "double
      // line" effect that gives the pen-area visual weight without thickness.
      const penAreaInner = new PIXI.Graphics();
      penAreaInner.rect(66, GOAL_LINE_Y + 6, 258, PEN_AREA_DEPTH - 12).stroke(guideStyle);
      app.stage.addChild(penAreaInner);

      const sixYard = new PIXI.Graphics();
      sixYard.rect(110, GOAL_LINE_Y, 170, SIX_YARD_DEPTH).stroke(innerStyle);
      app.stage.addChild(sixYard);

      const penArc = new PIXI.Graphics();
      penArc
        .arc(FIELD_W / 2, GOAL_LINE_Y + PEN_AREA_DEPTH, 55, 0, Math.PI, false)
        .stroke(innerStyle);
      app.stage.addChild(penArc);

      // Penalty spot — cyan 4px dot replaces the neutral white 3px dot.
      // Pulls into the §9.8 corner-glint accent family without violating
      // "cyan = accent only" (DESIGN.md §11) — this is a 4px detail.
      const spot = new PIXI.Graphics();
      spot.circle(BALL_X, BALL_Y, 4).fill(COLORS.accentCyan);
      app.stage.addChild(spot);

      // ─── GOAL ───
      const goal = new PIXI.Container();
      const goalLeft = 100;
      const goalRight = 290;
      const goalTop = 30;
      const goalBottom = 110;

      // Goal line bumped from 1px → 2px for visual heft (Wave 3 §4.4 D).
      const posts = new PIXI.Graphics();
      posts.rect(goalLeft, goalTop, 3, goalBottom - goalTop).fill(COLORS.textPrimary);
      posts.rect(goalRight - 3, goalTop, 3, goalBottom - goalTop).fill(COLORS.textPrimary);
      posts.rect(goalLeft, goalTop, goalRight - goalLeft, 2).fill(COLORS.textPrimary);
      goal.addChild(posts);

      // Net mesh — thin grid behind posts (drawn before posts visually = under).
      const net = new PIXI.Graphics();
      const netStroke = { color: COLORS.textPrimary, width: 0.8, alpha: 0.3 };
      const netInnerLeft = goalLeft + 3;
      const netInnerRight = goalRight - 3;
      const netTop = goalTop + 3;
      const netBottom = goalBottom;
      const verticalCount = 12;
      for (let i = 1; i < verticalCount; i++) {
        const x = netInnerLeft + (i * (netInnerRight - netInnerLeft)) / verticalCount;
        net.moveTo(x, netTop).lineTo(x, netBottom).stroke(netStroke);
      }
      const horizontalCount = 4;
      for (let j = 1; j < horizontalCount; j++) {
        const y = netTop + (j * (netBottom - netTop)) / horizontalCount;
        net.moveTo(netInnerLeft, y).lineTo(netInnerRight, y).stroke(netStroke);
      }
      // Net inserted under the goal posts container so posts overlay it visually.
      app.stage.addChildAt(net, app.stage.children.indexOf(spot) + 1);
      app.stage.addChild(goal);

      // ─── GUARDIAN ─── (Wave 3: hair stroke + shorts knot + glove highlight
      // + idle lean — see §4.4 B). Body proportions unchanged from baseline
      // §9.10 (torso + head + 2 arms). Total shape count goes 5 → 9 — still
      // "sign of the goalkeeper", not a character.
      const guardian = new PIXI.Container();

      const torso = new PIXI.Graphics();
      torso.roundRect(-12, -10, 24, 28, 4).fill(COLORS.actionPrimary);
      guardian.addChild(torso);

      // Shorts knot — single rounded-rect detail centred on the lower torso.
      // Same blue family, hairline edge keeps it readable on the torso fill.
      const shortsKnot = new PIXI.Graphics();
      shortsKnot
        .roundRect(-4, 10, 8, 5, 1)
        .fill(COLORS.actionPrimary)
        .stroke({ color: COLORS.surfaceMain, width: 0.6, alpha: 0.6 });
      guardian.addChild(shortsKnot);

      const head = new PIXI.Graphics();
      head.circle(0, -18, 6).fill(COLORS.actionPrimary);
      guardian.addChild(head);

      // Hair — two short action-primary strokes traced over the head top.
      // Each is a 1px line, deliberately offset so they read as a single tuft.
      const hair = new PIXI.Graphics();
      hair
        .moveTo(-4, -23)
        .lineTo(-1, -19)
        .stroke({ color: COLORS.actionPrimary, width: 1, alpha: 0.9 });
      hair
        .moveTo(3, -23)
        .lineTo(1, -19)
        .stroke({ color: COLORS.actionPrimary, width: 1, alpha: 0.9 });
      guardian.addChild(hair);

      const armL = new PIXI.Graphics();
      armL.roundRect(-22, -8, 8, 18, 3).fill(COLORS.actionPrimary);
      armL.circle(-18, 12, 4).stroke({ color: COLORS.accentCyan, width: 2 });
      // Glove top highlight — thin cyan stroke arc along the upper edge of
      // the glove, slightly thinner than the main stroke, gives the gloves
      // a "wet light" sheen without adding a new fill.
      armL.arc(-18, 12, 4, Math.PI, Math.PI * 2).stroke({ color: COLORS.accentCyan, width: 1, alpha: 0.7 });
      guardian.addChild(armL);

      const armR = new PIXI.Graphics();
      armR.roundRect(14, -8, 8, 18, 3).fill(COLORS.actionPrimary);
      armR.circle(18, 12, 4).stroke({ color: COLORS.accentCyan, width: 2 });
      armR.arc(18, 12, 4, Math.PI, Math.PI * 2).stroke({ color: COLORS.accentCyan, width: 1, alpha: 0.7 });
      guardian.addChild(armR);

      guardian.position.set(GUARDIAN_HOME_X, GUARDIAN_Y);
      app.stage.addChild(guardian);

      // ─── BALL SHADOW ─── (GDD §6 "тень под мячом — отдельный спрайт").
      // A dark-blue-tinted ellipse that tracks the ball in X/Y and shrinks as
      // the ball rises in synthetic Z during flight (inverse-Z scale). Drawn
      // before the ball so the ball sits on top. Blue-tint, not black, per
      // .cursorrules (no gray/black shadows).
      const ballShadow = new PIXI.Graphics();
      ballShadow.ellipse(0, 0, 11, 4).fill({ color: COLORS.actionPrimary, alpha: 0.28 });
      ballShadow.position.set(BALL_X, BALL_Y + 9);
      app.stage.addChild(ballShadow);

      // ─── BALL ─── classic 6-pentagon soccer ball: white circle base + one
      // dark pentagon at the centre + five smaller pentagons spaced evenly
      // around it. Unchanged from baseline — Wave 3 keeps the ball form.
      const ball = new PIXI.Container();
      const r = 11;
      const ballBase = new PIXI.Graphics();
      ballBase
        .circle(0, 0, r)
        .fill(COLORS.textPrimary)
        .stroke({ color: COLORS.surfaceMain, width: 0.75, alpha: 0.4 });
      ball.addChild(ballBase);

      const pentagon = (cx: number, cy: number, sz: number, rot: number) => {
        const pts: number[] = [];
        for (let i = 0; i < 5; i++) {
          const a = (i * Math.PI * 2) / 5 + rot;
          pts.push(cx + Math.cos(a) * sz, cy + Math.sin(a) * sz);
        }
        const g = new PIXI.Graphics();
        g.poly(pts).fill(COLORS.surfaceMain);
        return g;
      };

      ball.addChild(pentagon(0, 0, r * 0.32, -Math.PI / 2));

      const distFromCenter = r * 0.62;
      const surroundSize = r * 0.2;
      for (let i = 0; i < 5; i++) {
        const a = (i * Math.PI * 2) / 5 - Math.PI / 2 + Math.PI / 5;
        const cx = Math.cos(a) * distFromCenter;
        const cy = Math.sin(a) * distFromCenter;
        ball.addChild(pentagon(cx, cy, surroundSize, a + Math.PI));
      }

      ball.position.set(BALL_X, BALL_Y);
      app.stage.addChild(ball);

      // ─── MOUNT CANVAS ───
      const host = canvasHostRef.current;
      if (host) {
        host.appendChild(app.canvas);
        app.canvas.style.display = 'block';
        app.canvas.style.width = '100%';
        app.canvas.style.height = '100%';
      }

      // ─── SHOT STATE MACHINE ───
      // idle → (kick) → flying → resetting → idle, forever. No attempt cap.
      // matter-js is intentionally unused: a penalty flight is a straight
      // ball→goal interpolation; a hand-rolled lerp + synthetic sine Z-arc is
      // lighter and deterministic (GDD §6 ballZ(t) = sin(t·π)·peak).
      type Phase = 'idle' | 'flying' | 'resetting';
      let phase: Phase = 'idle';
      let elapsed = 0; // seconds, for idle oscillation
      let flightT = 0; // ms into the current flight
      let flightDur = FLIGHT_MS_MAX;
      let resetT = 0; // ms into the reset hold
      const start = { x: BALL_X, y: BALL_Y };
      const end = { x: BALL_X, y: GOAL_LINE_Y };
      let peakZ = 24; // synthetic flight height (px), from power
      // Keeper dive target (x offset from home) + lerp progress.
      let keeperTargetX = GUARDIAN_HOME_X;
      let keeperDiveT = 0;
      let keeperSide: 'left' | 'center' | 'right' = 'center';
      let pendingResult: ShotResult = 'miss'; // decided at kick → lets goals enter the net

      const resetBall = () => {
        ball.position.set(BALL_X, BALL_Y);
        ball.alpha = 1;
        ball.scale.set(1);
        ballShadow.position.set(BALL_X, BALL_Y + 9);
        ballShadow.scale.set(1);
        ballShadow.alpha = 0.28;
        keeperTargetX = GUARDIAN_HOME_X;
        keeperDiveT = 0;
        guardian.rotation = 0;
        phase = 'idle';
        busyRef.current = false;
      };

      // Resolve which goal third a terminus X falls in.
      const thirdOf = (x: number): 'left' | 'center' | 'right' => {
        const third = (GOAL_MOUTH_RIGHT - GOAL_MOUTH_LEFT) / 3;
        if (x < GOAL_MOUTH_LEFT + third) return 'left';
        if (x > GOAL_MOUTH_RIGHT - third) return 'right';
        return 'center';
      };

      // The kick. Called from the React pointer-up handler via kickRef.
      const kick = (targetX: number, targetY: number) => {
        if (phase !== 'idle' || busyRef.current) return;

        // Swipe vector is ball → release point (matches the on-screen hint).
        const dx = targetX - BALL_X;
        const dy = targetY - BALL_Y;
        const len = Math.hypot(dx, dy);

        // GDD §5 / §12: swipe < 40px doesn't count — soft "too weak" feedback,
        // no shot, no lock-out, stay idle so the user can immediately retry.
        if (len < MIN_SWIPE) {
          trySync(() => hapticFeedback.impactOccurred('light'));
          return;
        }

        busyRef.current = true;
        phase = 'flying';
        flightT = 0;

        // Power 0..1 from clamped swipe length → flight speed + peak height.
        const power = Math.min(len, MAX_SWIPE) / MAX_SWIPE;
        flightDur = FLIGHT_MS_MAX - (FLIGHT_MS_MAX - FLIGHT_MS_MIN) * power;
        peakZ = 12 + power * 48; // 12..60px visual height (GDD §6)

        // Direction: normalise, but FORCE upward travel toward the goal — a
        // penalty always goes "up" the field. We mirror any downward dy so a
        // careless down-swipe still sends the ball at the goal (GDD §5 clamp
        // into the 100° cone toward the goal), then aim at the goal line.
        const ux = dx / len;
        // Horizontal aim point on the goal line, clamped to just inside posts
        // so a wild sideways flick reads as "miss wide" rather than leaving
        // the field entirely.
        const reach = GOAL_LINE_Y; // flight always ends at the goal line in Y
        const travelY = BALL_Y - reach; // positive distance upfield
        // Project x using the swipe slope, scaled by how far we travel up.
        const slope = ux / Math.max(0.2, Math.abs(dy) / len); // guard near-horiz
        let aimX = BALL_X + slope * travelY * 0.6;
        // Allow a little outside the posts so "miss" is reachable, but clamp
        // hard so the ball never flies off-canvas.
        aimX = Math.max(GOAL_MOUTH_LEFT - 34, Math.min(GOAL_MOUTH_RIGHT + 34, aimX));

        start.x = BALL_X;
        start.y = BALL_Y;
        end.x = aimX;

        // ── Keeper AI (GDD §7) — decide AFTER the kick, ~60% correct side.
        const correctSide = thirdOf(aimX);
        const guessRight = Math.random() < 0.6;
        if (guessRight) {
          keeperSide = correctSide;
        } else {
          const others = (['left', 'center', 'right'] as const).filter((s) => s !== correctSide);
          keeperSide = others[Math.floor(Math.random() * others.length)];
        }
        keeperTargetX =
          keeperSide === 'left'
            ? GUARDIAN_HOME_X - 58
            : keeperSide === 'right'
              ? GUARDIAN_HOME_X + 58
              : GUARDIAN_HOME_X;
        keeperDiveT = 0;

        // Outcome decided now (deterministic from aim + keeper) so a GOAL flies
        // INTO the net past the goal line, not stops at it (Igor round-3 «мяч в
        // ворота»). Save/miss still terminate at the line.
        const landedThird = thirdOf(aimX);
        const inMouth = aimX >= GOAL_MOUTH_LEFT && aimX <= GOAL_MOUTH_RIGHT;
        pendingResult = !inMouth ? 'miss' : keeperSide === landedThird ? 'save' : 'goal';
        end.y = pendingResult === 'goal' ? GOAL_LINE_Y - 16 : reach;

        trySync(() => hapticFeedback.impactOccurred('medium')); // GDD §9 kick haptic
      };

      const onShotResolved = (result: ShotResult) => {
        if (result === 'goal') {
          setScore((s) => ({ ...s, you: s.you + 1 }));
          trySync(() => hapticFeedback.notificationOccurred('success'));
        } else {
          setScore((s) => ({ ...s, keeper: s.keeper + 1 }));
          trySync(() =>
            hapticFeedback.notificationOccurred(result === 'save' ? 'warning' : 'error'),
          );
        }
      };

      // ─── TICKER ─── drives idle oscillation, flight, keeper dive, reset.
      const tickerCb = (ticker: { deltaMS: number }) => {
        const dt = ticker.deltaMS;
        elapsed += dt / 1000;

        if (phase === 'idle') {
          // Ball micro-bobbing 1.0↔1.02 1.8s; keeper sway ±2px + ±3° lean.
          ball.scale.set(1 + Math.sin((elapsed / 1.8) * Math.PI * 2) * 0.02);
          guardian.x = GUARDIAN_HOME_X + Math.sin((elapsed / 2.4) * Math.PI * 2) * 2;
          guardian.rotation = Math.sin((elapsed / 4) * Math.PI * 2) * 0.052;
          ballShadow.position.set(BALL_X, BALL_Y + 9);
          return;
        }

        // Keeper dive — eased lerp toward the chosen third (both flight + reset
        // so the save pose holds during the result beat).
        keeperDiveT = Math.min(1, keeperDiveT + dt / 300);
        const ke = 1 - Math.pow(1 - keeperDiveT, 3); // ease-out cubic
        guardian.x = GUARDIAN_HOME_X + (keeperTargetX - GUARDIAN_HOME_X) * ke;
        guardian.rotation =
          keeperSide === 'left' ? -0.32 * ke : keeperSide === 'right' ? 0.32 * ke : 0;

        if (phase === 'flying') {
          flightT += dt;
          const t = Math.min(1, flightT / flightDur);
          // Linear X/Y travel with a synthetic Z (sine) lifting the ball.
          const x = start.x + (end.x - start.x) * t;
          const y = start.y + (end.y - start.y) * t;
          const z = Math.sin(t * Math.PI) * peakZ;
          ball.position.set(x, y - z);
          // Ball grows slightly with height; squash at the very start (kick).
          const squash = t < 0.12 ? 0.92 + (t / 0.12) * 0.08 : 1;
          ball.scale.set((1 + z / peakZ * 0.18) * squash);
          // Shadow tracks ground X/Y and shrinks as the ball rises.
          ballShadow.position.set(x, BALL_Y + 9);
          const shrink = 1 - (z / Math.max(1, peakZ)) * 0.55;
          ballShadow.scale.set(shrink);
          ballShadow.alpha = 0.28 * shrink;

          if (t >= 1) {
            // Outcome was decided at kick time (so goals fly into the net).
            onShotResolved(pendingResult);
            phase = 'resetting';
            resetT = 0;
          }
          return;
        }

        if (phase === 'resetting') {
          resetT += dt;
          // Hold the result pose, fade the ball out over the last 300ms.
          const fadeStart = RESET_DELAY_MS - 300;
          if (resetT > fadeStart) {
            ball.alpha = Math.max(0, 1 - (resetT - fadeStart) / 300);
            ballShadow.alpha = Math.max(0, ballShadow.alpha - dt / 300);
          }
          if (resetT >= RESET_DELAY_MS) {
            resetBall();
          }
        }
      };
      app.ticker.add(tickerCb);

      // Expose the kick to the React pointer-up handler.
      kickRef.current = kick;

      removeTicker = () => {
        app.ticker.remove(tickerCb);
        kickRef.current = null;
      };

      // ─── KEEPER SHADOW POSITION ───
      // The cyan ellipse under keeper is HTML (so we get the blur token); its
      // coordinates are derived from the canvas size. Centre-x follows the
      // keeper home, y sits just below the keeper figure baseline.
      const shadow = keeperShadowRef.current;
      if (shadow && host) {
        const placeShadow = () => {
          const rect = host.getBoundingClientRect();
          // Scale canvas px (FIELD_W × FIELD_H) into host px.
          const sx = rect.width / FIELD_W;
          const sy = rect.height / FIELD_H;
          shadow.style.left = `${GUARDIAN_HOME_X * sx}px`;
          shadow.style.top = `${(GUARDIAN_Y + 22) * sy}px`;
        };
        placeShadow();
        const ro = new ResizeObserver(placeShadow);
        ro.observe(host);
        const prevRemove = removeTicker;
        removeTicker = () => {
          prevRemove?.();
          ro.disconnect();
        };
      }
    })();

    return () => {
      cancelled = true;
      removeTicker?.();
      // Pixi 8 Application destroy signature: destroy(rendererDestroyOptions, options)
      const app = appRef as { destroy?: (a: boolean, b: { children: boolean }) => void } | null;
      app?.destroy?.(true, { children: true });
    };
  }, []);

  // ─── HINT-ARC GEOMETRY ───
  // Build a parabolic quadratic-bezier path from BALL to aim. Control point
  // is offset upward by half the dx so the arc bows toward the goal — feels
  // like a struck-ball trajectory rather than a straight pull.
  const arcPath = (() => {
    if (!aim) return null;
    const x0 = BALL_X;
    const y0 = BALL_Y;
    const x1 = aim.x;
    const y1 = aim.y;
    const midX = (x0 + x1) / 2;
    // Bow upward (toward smaller y, i.e., goal) by ~40% of the horizontal span,
    // clamped so very short pulls don't produce a sharp loop.
    const dx = Math.abs(x1 - x0);
    const bow = Math.min(120, Math.max(24, dx * 0.4));
    const midY = (y0 + y1) / 2 - bow;
    return `M ${x0} ${y0} Q ${midX} ${midY} ${x1} ${y1}`;
  })();

  return (
    <section
      className="module-04-crt relative min-h-full w-full bg-surface-main text-text-primary"
      style={{
        paddingTop: 'max(var(--safe-top, 0px), 0.75rem)',
        paddingBottom: 'max(var(--safe-bottom, 0px), 1.5rem)',
      }}
    >
      <AtmosphericLayer spheres={1} shafts={0} intensity="reduced" />

      {/* CRT scanlines overlay — Igor 2026-06-04: retro-vibe без полного pixel-art.
          Тонкие горизонтальные тёмные линии каждые 3px поверх всей секции,
          mix-blend-mode: multiply — мягко затемняет без обесцвечивания.
          z-index 5 = выше content (z-10 ниже? — нет, content внутри `z-10`
          относительно section, scanlines absolute over section).
          pointer-events none — клики проходят сквозь. */}
      <div className="module-04-crt-scanlines" aria-hidden />

      {/* CRT vignette — углы темнее центра, имитация старого ТВ. */}
      <div className="module-04-crt-vignette" aria-hidden />

      <div className="relative z-10 flex h-full flex-col">
        {/* HUD top — goals tally + sigil + mute. Igor 2026-06-11: attempts are
            INFINITE ("убрать попытки, сделать их бесконечными") — the old fixed
            "попытка 1/5" counter (and its implied 5-shot cap) is gone. We show a
            running "забито" goals tally instead: feedback without a lock-out. */}
        <div className="flex items-center justify-between px-sp-5 py-sp-3">
          <div className="font-ui text-body-sm text-text-muted">забито {score.you}</div>
          <div className="flex items-center gap-sp-3">
            <button
              type="button"
              aria-label="отключить звук"
              className="text-border-hairline transition-colors hover:text-text-primary"
            >
              <Volume2 className="h-[18px] w-[18px]" strokeWidth={1.5} />
            </button>
            <span className="opacity-40">
              <Sigil variant="hud" />
            </span>
          </div>
        </div>

        {/* Wave 3 — LED scoreboard. HTML overlay (not Pixi) keeps Lora 72px
            glyph quality at retina, and lets the cyan glow ride on text-shadow
            tokens. ТЫ left, ВРАТАРЬ right, score colon centred. */}
        <div className="motion-slide-in motion-stagger-1 module-04-scoreboard">
          <ScoreDigit value={score.you} side="left" />
          <span className="module-04-scoreboard__colon" aria-hidden>
            :
          </span>
          <ScoreDigit value={score.keeper} side="right" />
        </div>

        {/* Category title — Wave 3: shrink 30px → 22px Lora subhead, slide in
            with stagger-2. Single line per Igor — "smaller, floating in". */}
        <div className="px-sp-6 pb-sp-2 pt-sp-2 text-center">
          <h2 className="motion-slide-in motion-stagger-2 font-display text-[1.375rem] leading-[1.2] tracking-[-0.01em] text-text-primary">
            Геймификация в&nbsp;соц.сетях
          </h2>
        </div>

        {/* Pixi canvas + SVG hint-arc overlay + hint copy. Pointer handlers
            live on the host so any drag inside the canvas triggers the arc
            preview — a stub until physics lands (TODO above). */}
        <div
          ref={canvasHostRef}
          className="relative flex-1 self-stretch touch-none"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
        >
          {/* §9.10 cyan ellipse keeper shadow — absolute, positioned by JS. */}
          <div ref={keeperShadowRef} className="module-04-keeper-shadow" aria-hidden />

          {/* §9.9 hint-arc — only mounted while pointer is active.
              SVG viewBox matches the pixi stage so coordinates map 1:1. */}
          {arcPath && (
            <svg
              className="module-04-hint-arc"
              viewBox={`0 0 ${FIELD_W} ${FIELD_H}`}
              preserveAspectRatio="none"
              aria-hidden
            >
              <defs>
                <linearGradient id="module-04-hint-arc-stroke" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0%" stopColor="#0071E4" />
                  <stop offset="100%" stopColor="#0DD5FD" />
                </linearGradient>
              </defs>
              <path
                d={arcPath}
                stroke="url(#module-04-hint-arc-stroke)"
                strokeWidth={2}
                strokeDasharray="4 4"
                strokeLinecap="round"
                fill="none"
                opacity={0.7}
              />
              <circle
                cx={aim!.x}
                cy={aim!.y}
                r={8}
                fill="#0DD5FD"
                className="module-04-hint-arc__endpoint"
              />
            </svg>
          )}

          <div className="pointer-events-none absolute bottom-sp-6 left-0 right-0 z-10 text-center">
            <p className="motion-slide-in motion-stagger-4 font-ui text-[11px] leading-[1.5] text-text-muted">
              свайп от мяча → угол и сила удара
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}
